import { useEffect, useState, type CSSProperties } from "react";
import { useResolvedTheme } from "@/lib/theme";

// Colour roles. Each brand hue has a job, so colour carries meaning:
//   stages     early → late: cyan · azure · violet · pink · purple · Signed green
//   activities Call green · Email azure · Meeting violet · Note cyan · Hospitality purple · Task amber
//   handoffs   violet → pink
// Amber is reserved for "needs attention", green for success.
export type Role = "azure" | "violet" | "cyan" | "purple" | "pink" | "amber" | "green";

/** CSS colour for a role (theme-aware; use in `style`, not SVG presentation attributes). */
export function role(r: Role, alpha?: number): string {
  return alpha == null ? `rgb(var(--${r}))` : `rgb(var(--${r}) / ${alpha})`;
}

/** Inline style that sets `--c` for `.chip-tint`, `.avatar`, `.track-here` etc. */
export function roleVar(r: Role): CSSProperties {
  return { ["--c" as string]: `var(--${r})` } as CSSProperties;
}

const WON = new Set(["Signed"]);
const LOST = new Set(["Lost", "We rejected them"]);
const STAGE_ORDER: Role[] = ["cyan", "azure", "violet", "pink", "purple"];
const KNOWN_STAGES: Record<string, Role> = {
  lead: "cyan",
  qualified: "cyan",
  discovery: "cyan",
  demo: "azure",
  proposal: "violet",
  negotiation: "pink",
  contracting: "purple",
  // This org's pipeline: Discovery → Early → Advanced → Pending signature → MoU → Signed.
  "early discussion": "azure",
  "advanced discussion": "violet",
  "pending signature": "pink",
  mou: "purple",
};

export function isWon(stage: string): boolean {
  return WON.has(stage);
}
export function isLost(stage: string): boolean {
  return LOST.has(stage);
}

/** The colour role for a stage: by name when it's a common stage, else by its
 *  position in the pipeline, else a stable hash. */
export function stageRole(name: string, index?: number): Role {
  if (WON.has(name)) return "green";
  const known = KNOWN_STAGES[name.toLowerCase()];
  if (known) return known;
  if (index != null) return STAGE_ORDER[index % STAGE_ORDER.length];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return STAGE_ORDER[h % STAGE_ORDER.length];
}

/** CSS colour for a stage. An admin-set hex override wins. Lost stages are grey. */
export function stageColor(name: string, override?: string | null, alpha?: number): string {
  if (override) return alpha == null ? override : withAlpha(override, alpha);
  if (LOST.has(name))
    return alpha == null ? "rgb(var(--mut-500))" : `rgb(var(--mut-500) / ${alpha})`;
  return role(stageRole(name), alpha);
}

export function withAlpha(hex: string, alpha = 0.18): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
}

export const ACTIVITY_ROLES: Record<string, Role> = {
  call: "green",
  email: "azure",
  meeting: "violet",
  note: "cyan",
  hospitality: "purple",
  status_change: "cyan",
  task: "amber",
  handoff: "pink",
};

// Stable per-person avatar colour.
const PEOPLE: Role[] = ["violet", "azure", "cyan", "pink", "purple", "green"];
export function personRole(name: string | null | undefined): Role {
  const s = name ?? "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;
  return PEOPLE[h % PEOPLE.length];
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function readHex(varName: string): string {
  if (typeof document === "undefined") return "#888888";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${varName}`).trim();
  const [r, g, b] = raw.split(/\s+/).map(Number);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "#888888";
  return "#" + [r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("");
}

export type Palette = Record<Role | "text" | "muted" | "faint" | "grid", string>;

/** Resolved hex colours for the current theme — for charts and SVG attributes,
 *  which can't read CSS variables. Re-resolves when the theme changes. */
export function usePalette(): Palette {
  const theme = useResolvedTheme();
  const read = (): Palette => ({
    azure: readHex("azure"),
    violet: readHex("violet"),
    cyan: readHex("cyan"),
    purple: readHex("purple"),
    pink: readHex("pink"),
    amber: readHex("amber"),
    green: readHex("green"),
    text: readHex("mut-100"),
    muted: readHex("mut-300"),
    faint: readHex("mut-500"),
    grid: readHex("mut-700"),
  });
  const [palette, setPalette] = useState<Palette>(read);
  useEffect(() => {
    // Theme attribute is applied in an effect too; read on the next frame.
    const id = requestAnimationFrame(() => setPalette(read()));
    return () => cancelAnimationFrame(id);
  }, [theme]);
  return palette;
}
